/**
 * The site-wide campus selection, shared with the campus-picker widget
 * (see the `campus-aware-widget` skill). This widget only reads it: the
 * primary cookie is JSON, the legacy cookie is the old TBX header's base64,
 * and a same-page change arrives as a `campus-picker:changed` CustomEvent
 * on window. Copied from campus-picker's campus-storage.ts rather than
 * imported so the two bundles stay independent.
 */

const COOKIE_NAME = 'woodside-selected-campus';
const LEGACY_COOKIE = 'tbx-ws__selected-location';

export interface SelectedCampus {
  congregationId: number;
  /** "Lake Orion"; null when only the legacy cookie's id is known */
  name: string | null;
}

function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const match = document.cookie
    .split(';')
    .map((c) => c.trim())
    .find((c) => c.startsWith(`${name}=`));
  if (!match) return null;
  try {
    return decodeURIComponent(match.slice(name.length + 1));
  } catch {
    return match.slice(name.length + 1);
  }
}

export function getSelectedCampus(): SelectedCampus | null {
  try {
    const raw = readCookie(COOKIE_NAME);
    if (raw) {
      const data = JSON.parse(raw);
      if (data?.congregationId) {
        return {
          congregationId: Number(data.congregationId),
          name: typeof data.name === 'string' && data.name.trim() ? data.name.trim() : null,
        };
      }
    }
  } catch {
    /* fall through to legacy */
  }
  try {
    const raw = readCookie(LEGACY_COOKIE);
    if (!raw) return null;
    const data = JSON.parse(atob(raw));
    if (!data?.location_id) return null;
    const name = data.location_short_name || data.location_name;
    return {
      congregationId: Number(data.location_id),
      name: typeof name === 'string' && name.trim() ? name.trim() : null,
    };
  } catch {
    return null;
  }
}

/**
 * Follow the campus picker: same-page picks arrive as the event, picks made
 * in another tab or on another subdomain show up in the cookie when the
 * page is next looked at.
 */
export function onCampusChange(handler: (campus: SelectedCampus | null) => void): () => void {
  function onEvent(e: Event) {
    const detail = (e as CustomEvent).detail;
    if (detail?.congregationId) {
      handler({
        congregationId: Number(detail.congregationId),
        name: typeof detail.name === 'string' && detail.name.trim() ? detail.name.trim() : null,
      });
    }
  }
  const onLook = () => handler(getSelectedCampus());
  window.addEventListener('campus-picker:changed', onEvent);
  window.addEventListener('focus', onLook);
  document.addEventListener('visibilitychange', onLook);
  return () => {
    window.removeEventListener('campus-picker:changed', onEvent);
    window.removeEventListener('focus', onLook);
    document.removeEventListener('visibilitychange', onLook);
  };
}
